/**
 * BatchConversionResults Component
 *
 * Shows results for a batch of converted files with per-file download.
 */

import { memo, useCallback, useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Download,
  RefreshCw,
  FileAudio,
  FileVideo,
  FolderOpen,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { isDesktopApp } from '../../utils/apiMode';
import { openVideoFolder as electronOpenVideoFolder } from '../../utils/electronBridge';
import { formatFileSize } from '@/constants/fileSize';
import type { BatchFile } from './types';
import { downloadFile } from '@/utils/downloadProxy';
import { getConversionDownloadUrl } from '../../hooks/useMediaConversion';

interface BatchConversionResultsProps {
  files: BatchFile[];
  onConvertMore: () => void;
}

export const BatchConversionResults = memo(function BatchConversionResults({
  files,
  onConvertMore,
}: BatchConversionResultsProps) {
  const { t } = useTranslation();
  const isDesktop = isDesktopApp();

  const successCount = files.filter((f) => f.status === 'complete').length;
  const errorCount = files.filter((f) => f.status === 'error').length;
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const handleDownload = useCallback(
    async (jobId: string, outputFileName: string, fileId: string) => {
      if (downloadingIds.has(fileId)) return;
      setDownloadingIds((prev) => new Set(prev).add(fileId));
      try {
        // The link that came with the result may have expired; ask for a fresh one.
        await downloadFile(await getConversionDownloadUrl(jobId), outputFileName);
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
    },
    [downloadingIds]
  );

  const handleOpenFolder = useCallback(
    async (filePath: string) => {
      if (isDesktop && filePath) {
        await electronOpenVideoFolder(filePath);
      }
    },
    [isDesktop]
  );

  const handleDownloadAll = useCallback(async () => {
    if (isDownloadingAll) return;
    setIsDownloadingAll(true);
    try {
      await Promise.all(
        files
          .filter((f) => f.status === 'complete' && f.result?.downloadUrl)
          .map(async (f) =>
            downloadFile(await getConversionDownloadUrl(f.result!.jobId), f.result!.outputFileName)
          )
      );
    } finally {
      setIsDownloadingAll(false);
    }
  }, [files, isDownloadingAll]);

  return (
    <div className="card-themed-lg">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full mb-4">
          <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-xl font-semibold text-secondary-900 dark:text-white">
          {t('converter.batch.complete', { defaultValue: 'Batch conversion complete!' })}
        </h3>
        <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
          {successCount > 0 &&
            t('converter.batch.successCount', {
              defaultValue: '{{count}} file(s) converted successfully',
              count: successCount,
            })}
          {errorCount > 0 && (
            <span className="text-red-500 ml-2">
              {t('converter.batch.errorCount', {
                defaultValue: '{{count}} failed',
                count: errorCount,
              })}
            </span>
          )}
        </p>
      </div>

      {/* File results list */}
      <div className="space-y-3 mb-6">
        {files.map((batchFile) => {
          const Icon = batchFile.mediaType === 'video' ? FileVideo : FileAudio;
          const iconColor = batchFile.mediaType === 'video' ? 'text-blue-500' : 'text-purple-500';

          return (
            <div
              key={batchFile.id}
              className="flex items-center gap-3 p-3 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg"
            >
              {/* Status */}
              {batchFile.status === 'complete' ? (
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              )}

              {/* File icon */}
              <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                  {batchFile.result?.outputFileName || batchFile.file.name}
                </p>
                {batchFile.status === 'complete' && batchFile.result && (
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    {batchFile.result.outputSize > 0 && formatFileSize(batchFile.result.outputSize)}
                  </p>
                )}
                {batchFile.status === 'error' && (
                  <p className="text-xs text-red-500 truncate">{batchFile.error}</p>
                )}
              </div>

              {/* Download button */}
              {batchFile.status === 'complete' && batchFile.result?.downloadUrl && (
                <div className="flex-shrink-0">
                  {/* Reveal only when the file is genuinely on this machine.
                      The old `outputPath || downloadUrl` fallback handed an
                      http:// URL to shell.showItemInFolder(), a silent no-op. */}
                  {isDesktop && batchFile.result!.outputPath ? (
                    <button
                      onClick={() => void handleOpenFolder(batchFile.result!.outputPath!)}
                      className="p-2 rounded-lg hover:bg-secondary-200 dark:hover:bg-secondary-600 transition-colors"
                      title="Show in Folder"
                    >
                      <FolderOpen className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                    </button>
                  ) : (
                    <button
                      onClick={() =>
                        void handleDownload(
                          batchFile.result!.jobId,
                          batchFile.result!.outputFileName,
                          batchFile.id
                        )
                      }
                      disabled={downloadingIds.has(batchFile.id)}
                      className="p-2 rounded-lg hover:bg-secondary-200 dark:hover:bg-secondary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download"
                    >
                      {downloadingIds.has(batchFile.id) ? (
                        <Loader2 className="w-4 h-4 text-secondary-600 dark:text-secondary-400 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 text-secondary-600 dark:text-secondary-400" />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        {successCount > 1 && (
          <button
            onClick={() => void handleDownloadAll()}
            disabled={isDownloadingAll}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-secondary-400 text-white font-medium rounded-lg transition-colors"
          >
            {isDownloadingAll ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            {isDownloadingAll
              ? t('common.downloading', { defaultValue: 'Downloading...' })
              : t('converter.batch.downloadAll', { defaultValue: 'Download All' })}
          </button>
        )}
        <button
          onClick={onConvertMore}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700 font-medium rounded-lg transition-colors"
        >
          <RefreshCw className="w-5 h-5" />
          {t('converter.batch.convertMore', { defaultValue: 'Convert More Files' })}
        </button>
      </div>
    </div>
  );
});

export default BatchConversionResults;
